import React, { memo, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { areEqual } from 'Utils/equalityChecks';
import { Api } from 'Utils/api';
import { Modal } from 'Components/Modal';

import { CompanyFormData, Option } from '../types';

import classes from './addCompanyModal.module.css';

interface AddCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  ssoToken: string;
  onCompanyCreated: (company: Option) => void;
}

const defaultFormValues: CompanyFormData = {
  locationId: '',
  name: '',
  email: '',
  dialCode: '+1',
  phone: '',
  address: '',
  city: '',
  state: '',
  postalCode: '',
};

function AddCompanyModalComponent({
  isOpen,
  onClose,
  locationId,
  ssoToken,
  onCompanyCreated,
}: AddCompanyModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm<CompanyFormData>({
    mode: 'onChange',
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    if (!isOpen) {
      setErrorMessage(null);
      setSuccessMessage(null);
      reset(defaultFormValues);
    }
  }, [isOpen, reset]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const onSubmit = handleSubmit(async (data) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      // Backend expects snake_case field names
      const payload: any = {
        name: data.name,
      };

      if (data.email?.trim()) {
        payload.email = data.email;
      }
      if (data.phone?.trim()) {
        payload.phone = `${data.dialCode} ${data.phone}`;
      }
      if (data.address?.trim()) {
        payload.address = data.address;
      }
      if (data.city?.trim()) {
        payload.city = data.city;
      }
      if (data.state?.trim()) {
        payload.state = data.state;
      }
      if (data.postalCode?.trim()) {
        payload.postal_code = data.postalCode;
      }

      const response = await Api.post('/ghl/embed/businesses', payload, {
        headers: { 'X-GHL-SSO': ssoToken },
      });

      const business = response?.data?.business || response?.data?.data || response?.data;
      const businessId = business?.id;

      if (!businessId) {
        throw new Error('Failed to create company');
      }

      onCompanyCreated({
        id: businessId,
        label: business?.name || data.name,
      });

      setSuccessMessage('Company created successfully!');

      setTimeout(() => {
        setSuccessMessage(null);
        reset(defaultFormValues);
      }, 3000);
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to create company', error);
      const message = error?.response?.data?.message || error?.message || 'Failed to create company';
      const matchingField = error?.response?.data?.meta?.matchingField;
      setErrorMessage(`${message}${matchingField ? ` Matching ${matchingField} already exists.` : ''}`);
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <Modal
      id="add-company-modal"
      classes={classes}
      title="Add Company"
      isOpen={isOpen}
      modalHeader
      modalCloseClick={handleClose}
    >
      {errorMessage && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {errorMessage}
          <button type="button" className="btn-close" onClick={() => setErrorMessage(null)} aria-label="Close" />
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {successMessage}
          <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)} aria-label="Close" />
        </div>
      )}

      <form onSubmit={onSubmit}>
        <div className="mb-3">
          <label htmlFor="company-name" className="form-label">
            Company Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
            id="company-name"
            {...register('name', { required: 'Company name is required' })}
          />
          {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label htmlFor="company-email" className="form-label">Email</label>
            <input
              type="email"
              className={`form-control ${errors.email ? 'is-invalid' : ''}`}
              id="company-email"
              {...register('email', {
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: 'Invalid email format',
                },
              })}
            />
            {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="company-phone" className="form-label">Phone</label>
            <div className="input-group">
              <select className="form-select" style={{ maxWidth: '80px' }} {...register('dialCode')}>
                <option value="+1">+1</option>
                <option value="+44">+44</option>
                <option value="+61">+61</option>
              </select>
              <input
                type="tel"
                className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                id="company-phone"
                placeholder="555-555-5555"
                {...register('phone', {
                  pattern: {
                    value: /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/,
                    message: 'Invalid phone format',
                  },
                })}
              />
            </div>
            {errors.phone && <div className="text-danger small">{errors.phone.message}</div>}
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="company-address" className="form-label">Address</label>
          <input type="text" className="form-control" id="company-address" {...register('address')} />
        </div>

        <div className="row">
          <div className="col-md-4 mb-3">
            <label htmlFor="company-city" className="form-label">City</label>
            <input type="text" className="form-control" id="company-city" {...register('city')} />
          </div>
          <div className="col-md-4 mb-3">
            <label htmlFor="company-state" className="form-label">State</label>
            <input type="text" className="form-control" id="company-state" {...register('state')} />
          </div>
          <div className="col-md-4 mb-3">
            <label htmlFor="company-postalCode" className="form-label">Zip Code</label>
            <input type="text" className="form-control" id="company-postalCode" {...register('postalCode')} />
          </div>
        </div>

        <button
          type="submit"
          className="btn btn-primary w-100"
          disabled={!isDirty || !isValid || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              Creating...
            </>
          ) : (
            'Create Company'
          )}
        </button>
      </form>
    </Modal>
  );
}

const AddCompanyModalMemo = memo(AddCompanyModalComponent, areEqual);

export { AddCompanyModalMemo as AddCompanyModal };
